import * as XLSX from 'xlsx'

export const replicateFirstRowSegments = (loadedData) => {
    if (!loadedData || loadedData.length === 0)
        return loadedData

    const firstSegment = loadedData[0].segmentos
    return loadedData.map(item => ({
        ...item,
        segmentos: firstSegment
    }))
}

const textNoiseRegex = /[\u200B-\u200D\uFEFF\u0000-\u001F\u007F-\u009F\uFFFD]+/g
const spreadsheetExtensions = ['xls', 'xlsx']
const mojibakeRegex = /(Ã.|Â.|â.|�)/g
const latinAccentedRegex = /[\u00C0-\u00FF\u0100-\u024F]/g
const windows1252ReverseMap = {
    '\u20AC': 0x80,
    '\u201A': 0x82,
    '\u0192': 0x83,
    '\u201E': 0x84,
    '\u2026': 0x85,
    '\u2020': 0x86,
    '\u2021': 0x87,
    '\u02C6': 0x88,
    '\u2030': 0x89,
    '\u0160': 0x8A,
    '\u2039': 0x8B,
    '\u0152': 0x8C,
    '\u017D': 0x8E,
    '\u2018': 0x91,
    '\u2019': 0x92,
    '\u201C': 0x93,
    '\u201D': 0x94,
    '\u2022': 0x95,
    '\u2013': 0x96,
    '\u2014': 0x97,
    '\u02DC': 0x98,
    '\u2122': 0x99,
    '\u0161': 0x9A,
    '\u203A': 0x9B,
    '\u0153': 0x9C,
    '\u017E': 0x9E,
    '\u0178': 0x9F
}

const countMatches = (value, regex) => (value.match(regex) || []).length

const getEncodingIssueScore = (value) => {
    return countMatches(value, mojibakeRegex) + countMatches(value, /\uFFFD/g) * 3
}

const getTextQualityScore = (value) => {
    return countMatches(value, latinAccentedRegex) - getEncodingIssueScore(value) * 10
}

const encodeWindows1252 = (value) => {
    const bytes = []

    for (const char of value) {
        const code = char.charCodeAt(0)
        if (code <= 0xFF) {
            bytes.push(code)
            continue
        }

        if (char in windows1252ReverseMap) {
            bytes.push(windows1252ReverseMap[char])
            continue
        }

        return null
    }

    return new Uint8Array(bytes)
}

export const repairMojibake = (value = '') => {
    const text = value === null || value === undefined ? '' : String(value)
    const score = getEncodingIssueScore(text)

    if (!score)
        return text

    const bytes = encodeWindows1252(text)
    if (!bytes)
        return text

    try {
        const repaired = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
        return getEncodingIssueScore(repaired) < score ? repaired : text
    } catch (err) {
        return text
    }
}

const decodeSingleByteText = (buffer) => {
    const labels = ['windows-1252', 'iso-8859-1', 'macintosh']
    let bestText = ''
    let bestScore = -Infinity

    labels.forEach(label => {
        try {
            const text = new TextDecoder(label).decode(buffer)
            const score = getTextQualityScore(text)
            if (score > bestScore) {
                bestText = text
                bestScore = score
            }
        } catch (err) {
            return
        }
    })

    return bestText || new TextDecoder().decode(buffer)
}

const decodeUtf16Text = (buffer) => {
    const bytes = new Uint8Array(buffer)
    const hasLittleEndianBom = bytes[0] === 0xFF && bytes[1] === 0xFE
    const hasBigEndianBom = bytes[0] === 0xFE && bytes[1] === 0xFF

    if (!hasLittleEndianBom && !hasBigEndianBom)
        return null

    try {
        return new TextDecoder(hasLittleEndianBom ? 'utf-16le' : 'utf-16be').decode(buffer)
    } catch (err) {
        return null
    }
}

export const decodeImportedText = (buffer) => {
    let text = decodeUtf16Text(buffer)

    if (text === null) {
        try {
            text = new TextDecoder('utf-8', { fatal: true }).decode(buffer)
        } catch (err) {
            text = decodeSingleByteText(buffer)
        }
    }

    if (text.charCodeAt(0) === 0xFEFF)
        text = text.slice(1)

    return repairMojibake(text)
}

const getFileExtension = (file) => {
    const name = file?.name || ''
    const parts = name.split('.')
    return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

const isSpreadsheetFile = (file) => {
    return spreadsheetExtensions.includes(getFileExtension(file))
}

const convertSpreadsheetToCsv = (buffer) => {
    const workbook = XLSX.read(buffer, { type: 'array' })
    const firstSheetName = workbook.SheetNames[0]

    if (!firstSheetName)
        return ''

    const worksheet = workbook.Sheets[firstSheetName]
    return XLSX.utils.sheet_to_csv(worksheet, { FS: ',', RS: '\n', blankrows: false })
}

export const readImportedFileAsText = (file, buffer) => {
    if (isSpreadsheetFile(file))
        return convertSpreadsheetToCsv(buffer)

    return decodeImportedText(buffer)
}

export const clearImportedText = (value = '') => {
    return repairMojibake(value).replace(textNoiseRegex, '')
}

export const normalizeImportHeader = (value = '', normalizeSeparators = false) => {
    let normalized = clearImportedText(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['"]/g, '')

    if (normalizeSeparators)
        normalized = normalized.replace(/[_\-]+/g, ' ')

    return normalized
        .replace(/\s+/g, ' ')
        .trim()
}
